
import React, { useState } from 'react';
import styled from 'styled-components';


/*
====================================
CONFIGURACIÓN DE LOS CAMPOS
====================================

Los tres campos de la adenda permiten
hasta 10.000 caracteres.

El límite es visible para el profesional
mediante el contador de cada campo.
====================================
*/

const MAX_CARACTERES_ADENDA = 10000;


/*
====================================
ESTILOS DEL MODAL
====================================
*/

const FondoModal = styled.div`
    position: fixed;
    inset: 0;
    z-index: 2000;

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 20px;

    background: rgba(0, 0, 0, 0.45);
`;


const VentanaModal = styled.div`
    width: 100%;
    max-width: 620px;
    max-height: calc(100vh - 40px);

    overflow-y: auto;

    padding: 26px;

    border-radius: 14px;
    background: #FFFFFF;

    box-shadow: 0 12px 38px rgba(0, 0, 0, 0.18);

    @media (max-width: 600px) {
        padding: 18px;
    }
`;


const TituloModal = styled.h2`
    margin: 0 0 12px;

    color: #23AAAF;
    font-size: 20px;
`;


const TextoModal = styled.p`
    margin: 0 0 18px;

    color: #646464;
    font-size: 14px;
    line-height: 1.5;
`;


const CampoModal = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;

    margin-bottom: 17px;
`;


const EtiquetaModal = styled.label`
    color: #646464;
    font-size: 13px;
    font-weight: 600;
`;


const TextoCampo = styled.textarea`
    box-sizing: border-box;

    width: 100%;
    min-height: 75px;

    padding: 11px 12px;

    border: 1px solid #D6DEDF;
    border-radius: 8px;

    color: #424242;
    font: inherit;
    font-size: 13px;
    line-height: 1.5;

    resize: vertical;

    &:focus {
        outline: 2px solid rgba(35, 170, 175, 0.2);
        border-color: #23AAAF;
    }
`;


/*
====================================
CONTADOR DE CARACTERES
====================================

Mostramos la cantidad utilizada y
el máximo permitido en cada campo.
====================================
*/

const ContadorCaracteres = styled.span`
    align-self: flex-end;

    color: #777777;
    font-size: 11px;
    line-height: 1.4;
`;


const AccionesModal = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;

    margin-top: 24px;
`;


const BotonModal = styled.button`
    padding: 10px 17px;

    border: 1px solid
        ${props => props.$principal ? '#23AAAF' : '#D6DEDF'};

    border-radius: 8px;

    background:
        ${props => props.$principal ? '#23AAAF' : '#FFFFFF'};

    color:
        ${props => props.$principal ? '#FFFFFF' : '#646464'};

    font-size: 13px;
    font-weight: 600;

    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;


/*
====================================
MODAL DE ADENDA
====================================

Este componente no guarda información
directamente en el backend.

Devuelve los campos de la adenda al
formulario que lo abrió.

La Anamnesis y su adenda se guardarán
juntas mediante la action correspondiente.
====================================
*/

const ModalAdendaAnamnesis = ({
    tipoDocumento,
    guardando = false,
    onCancelar,
    onConfirmar
}) => {

    const [
        informacionModificada,
        setInformacionModificada
    ] = useState('');

    const [motivo, setMotivo] =
        useState('');

    const [contenido, setContenido] =
        useState('');


    /*
    ====================================
    IDENTIFICAR DOCUMENTO
    ====================================
    */

    const nombreDocumento =
        tipoDocumento === 'semiestructurada'
            ? 'entrevista semiestructurada'
            : 'Anamnesis estructurada';


    /*
    ====================================
    CONFIRMAR GUARDADO
    ====================================

    Permitimos completar los tres campos,
    uno o dos, o dejarlos todos vacíos.

    El backend registra una constancia
    automática si el profesional no
    detalla los cambios.
    ====================================
    */

    const confirmarGuardado = () => {

        if (guardando) {
            return;
        }

        onConfirmar({

            informacion_modificada:
                informacionModificada.trim(),

            motivo:
                motivo.trim(),

            contenido:
                contenido.trim()

        });

    };


    const sinDetalle =
        !informacionModificada.trim() &&
        !motivo.trim() &&
        !contenido.trim();


    return (

        <FondoModal>

            <VentanaModal
                role="dialog"
                aria-modal="true"
                aria-labelledby="titulo-modal-adenda"
            >

                {/* =====================================
                    ENCABEZADO
                ====================================== */}

                <TituloModal id="titulo-modal-adenda">
                    Registrar modificación
                </TituloModal>


                <TextoModal>

                    Ya transcurrieron 24 horas desde
                    el primer guardado de la{' '}
                    {nombreDocumento}.

                    <br />
                    <br />

                    Para guardar los cambios, podés
                    completar una adenda o confirmar
                    la modificación sin detallar
                    qué cambiaste.

                    En ambos casos quedará registrado
                    que realizaste un nuevo guardado.

                </TextoModal>


                {/* =====================================
                    INFORMACIÓN MODIFICADA
                ====================================== */}

                <CampoModal>

                    <EtiquetaModal
                        htmlFor="adenda-informacion"
                    >
                        Información modificada o ampliada
                        (opcional)
                    </EtiquetaModal>

                    <TextoCampo
                        id="adenda-informacion"
                        rows={3}
                        maxLength={MAX_CARACTERES_ADENDA}
                        value={informacionModificada}
                        onChange={e =>
                            setInformacionModificada(
                                e.target.value
                            )
                        }
                        placeholder={
                            'Ej.: Se corrigió la edad de inicio de la marcha.'
                        }
                        disabled={guardando}
                    />

                    <ContadorCaracteres>
                        {informacionModificada.length}
                        {' / '}
                        {MAX_CARACTERES_ADENDA}
                        {' caracteres'}
                    </ContadorCaracteres>

                </CampoModal>


                {/* =====================================
                    MOTIVO
                ====================================== */}

                <CampoModal>

                    <EtiquetaModal
                        htmlFor="adenda-motivo"
                    >
                        Motivo (opcional)
                    </EtiquetaModal>

                    <TextoCampo
                        id="adenda-motivo"
                        rows={2}
                        maxLength={MAX_CARACTERES_ADENDA}
                        value={motivo}
                        onChange={e =>
                            setMotivo(
                                e.target.value
                            )
                        }
                        placeholder={
                            'Ej.: Aclaración realizada durante una entrevista posterior.'
                        }
                        disabled={guardando}
                    />

                    <ContadorCaracteres>
                        {motivo.length}
                        {' / '}
                        {MAX_CARACTERES_ADENDA}
                        {' caracteres'}
                    </ContadorCaracteres>

                </CampoModal>


                {/* =====================================
                    CONTENIDO
                ====================================== */}

                <CampoModal>

                    <EtiquetaModal
                        htmlFor="adenda-contenido"
                    >
                        Contenido de la adenda (opcional)
                    </EtiquetaModal>

                    <TextoCampo
                        id="adenda-contenido"
                        rows={5}
                        maxLength={MAX_CARACTERES_ADENDA}
                        value={contenido}
                        onChange={e =>
                            setContenido(
                                e.target.value
                            )
                        }
                        placeholder={
                            'Escribí la aclaración, corrección o ampliación que querés registrar.'
                        }
                        disabled={guardando}
                    />

                    <ContadorCaracteres>
                        {contenido.length}
                        {' / '}
                        {MAX_CARACTERES_ADENDA}
                        {' caracteres'}
                    </ContadorCaracteres>

                </CampoModal>


                {/* =====================================
                    AVISO DE RESPONSABILIDAD
                ====================================== */}

                <TextoModal>

                    {sinDetalle
                        ? (
                            'Si guardás sin completar los campos, ' +
                            'quedará registrado que modificaste el ' +
                            'documento sin especificar los cambios.'
                        )
                        : (
                            'Se guardarán los campos que completaste. ' +
                            'Los que dejes vacíos figurarán como ' +
                            'no especificados.'
                        )
                    }

                </TextoModal>


                {/* =====================================
                    BOTONES
                ====================================== */}

                <AccionesModal>

                    <BotonModal
                        type="button"
                        onClick={onCancelar}
                        disabled={guardando}
                    >
                        Cancelar
                    </BotonModal>


                    <BotonModal
                        type="button"
                        $principal
                        onClick={confirmarGuardado}
                        disabled={guardando}
                    >

                        {guardando
                            ? 'Guardando...'
                            : sinDetalle
                                ? 'Guardar sin detallar cambios'
                                : 'Guardar con adenda'
                        }

                    </BotonModal>

                </AccionesModal>

            </VentanaModal>

        </FondoModal>

    );

};


export default ModalAdendaAnamnesis;